import { Button } from "@climatepartner/components/Button";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Box, Stack, TextField, Typography } from "@mui/material";
import { ReactElement, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { useForwardInvite } from "../api/use-forward-invite";
import { InvitationCard } from "./account-creation-form";
import { ErrorCard } from "./error-card";
import { BUYER_NAME_FALLBACK } from "./invitation-header-card";

export interface ForwardFormProps {
  connectionRequestId: string;
  token: string;
  /** Only consumed by the invitation header, like `requestDueDate`; optional so callers that hide it can omit it. */
  buyerName?: string;
  requestDueDate?: string;
  heading?: string;
  description?: string;
  showInvitationHeader?: boolean;
  showFieldsHeading?: boolean;
  onForwarded?: () => void;
}

const DEFAULT_HEADING = "Invite a colleague";
const DEFAULT_DESCRIPTION =
  "If you're not the right person to provide this data, invite a colleague below to create a ClimatePartner account and respond instead.";

const forwardFormSchema = z.object({
  name: z.string().trim().min(1, "Contact name is required"),
  email: z.string().trim().min(1, "Email is required").email("Invalid email address"),
});

type ForwardFormValues = z.infer<typeof forwardFormSchema>;

const FORWARD_EMAIL_CONFLICT_MESSAGE = "This email can't be used for this request.";

const cardSx = {
  borderRadius: 2,
  border: 1,
  borderColor: "divider",
  width: "100%",
} as const;

// Same card treatment as the invitation header above the form (Figma 1340-42496).
const ForwardConfirmationCard = (): ReactElement => (
  <Box sx={{ borderRadius: 2, bgcolor: "background.paper", px: 2.5, py: 2.5, width: "100%" }}>
    <Typography
      variant="caption"
      sx={{ display: "block", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.8px", mb: 1 }}
    >
      Invitation
    </Typography>
    <Typography variant="h4" sx={{ mb: 1 }}>
      Contact person successfully changed
    </Typography>
    <Typography variant="body1" sx={{ color: "text.secondary" }}>
      An invite was sent to the specified contact person via email.
      <br />
      You can now close this tab.
    </Typography>
  </Box>
);

type ForwardScreen = "form" | "confirmation" | "invalid-link";

export const ForwardForm = ({
  connectionRequestId,
  token,
  buyerName,
  requestDueDate,
  heading = DEFAULT_HEADING,
  description = DEFAULT_DESCRIPTION,
  showInvitationHeader = true,
  showFieldsHeading = true,
  onForwarded,
}: ForwardFormProps): ReactElement => {
  const [screen, setScreen] = useState<ForwardScreen>("form");
  const { mutate, isPending, error: mutationError } = useForwardInvite();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ForwardFormValues>({
    resolver: zodResolver(forwardFormSchema),
    defaultValues: { name: "", email: "" },
  });

  const [name, email] = watch(["name", "email"]);
  const canSubmit = name.trim().length > 0 && email.trim().length > 0;

  // 404 is a one-time transition to the invalid-link screen, unlike 409/503 which keep the
  // form mounted so the user can correct and resubmit.
  useEffect(() => {
    if (mutationError === 404) {
      setScreen("invalid-link");
    }
  }, [mutationError]);

  const onSubmit = async (values: ForwardFormValues): Promise<void> => {
    try {
      await mutate({ connectionRequestId, token, name: values.name, email: values.email });
      setScreen("confirmation");
      onForwarded?.();
    } catch {
      // Error surfaced via the hook's typed `error`; nothing else to do here.
    }
  };

  if (screen === "confirmation") {
    return (
      <Stack sx={{ maxWidth: 560, width: "100%", mx: "auto" }}>
        <ForwardConfirmationCard />
      </Stack>
    );
  }

  if (screen === "invalid-link") {
    return (
      <Stack sx={{ maxWidth: 560, width: "100%", mx: "auto" }}>
        <ErrorCard variant="invalid-invite" />
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ maxWidth: 560, width: "100%", mx: "auto" }}>
      {showInvitationHeader && (
        <InvitationCard buyerName={buyerName ?? BUYER_NAME_FALLBACK} requestDueDate={requestDueDate ?? ""} />
      )}

      <Box sx={{ ...cardSx, bgcolor: "background.paper", px: 2.5, py: 3 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          {heading}
        </Typography>
        <Alert severity="info" sx={{ borderRadius: 1, mb: 2 }}>
          {description}
        </Alert>
        {showFieldsHeading && (
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Who should this request go to?
          </Typography>
        )}
        <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mb: 2 }}>
          Once you enter a new email, this invitation will become invalid. The new contact will get their own link to
          respond.
        </Typography>

        <Stack component="form" spacing={2} onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Contact name"
                required
                fullWidth
                size="small"
                error={Boolean(errors.name)}
                helperText={errors.name?.message}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email"
                required
                fullWidth
                size="small"
                error={Boolean(errors.email) || mutationError === 409}
                helperText={
                  errors.email?.message ?? (mutationError === 409 ? FORWARD_EMAIL_CONFLICT_MESSAGE : undefined)
                }
              />
            )}
          />

          {mutationError === 503 && (
            <Typography color="error" variant="body2">
              Something went wrong. Please try again.
            </Typography>
          )}

          <Button onClick={handleSubmit(onSubmit)} fullWidth disabled={!canSubmit || isPending} isLoading={isPending}>
            Forward request →
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};
